import { useState } from "react";
import { Calculator, Sparkles, Loader2, TrendingUp, FileText, IndianRupee } from "lucide-react";
import { generateTaxSummary, type AITaxSummary } from "@/lib/ai-engine";
import { formatINR } from "@/data/sample";

interface Props {
  totalDonated: number;
}

export function AITaxAdvisor({ totalDonated }: Props) {
  const [summary, setSummary] = useState<AITaxSummary | null>(null);
  const [loading, setLoading] = useState(false);

  async function calculate() {
    setLoading(true);
    setSummary(null);
    try {
      const r = await generateTaxSummary(totalDonated);
      setSummary(r);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-2xl border border-green-200 overflow-hidden shadow-sm">
      <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-700 to-teal-700 px-4 py-3.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20">
          <Calculator className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">AI Tax Advisor</p>
          <p className="text-[10px] text-white/70">Personalised 80G deduction analysis</p>
        </div>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold text-white flex items-center gap-1">
          <Sparkles className="h-2.5 w-2.5" /> AI
        </span>
      </div>

      <div className="bg-white p-4 space-y-3">
        {!summary && !loading && (
          <div className="text-center py-4">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
              <IndianRupee className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-[var(--fg)]">Calculate your 80G tax savings</p>
            <p className="mt-1 text-xs text-foreground/60">
              Based on {formatINR(totalDonated)} in total donations
            </p>
            <button
              onClick={calculate}
              className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 transition mx-auto"
            >
              <Sparkles className="h-4 w-4" /> Calculate Tax Savings
            </button>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-6 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            <span className="text-sm text-foreground/70">Computing tax savings...</span>
          </div>
        )}

        {summary && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-emerald-50 p-3 text-center">
                <p className="text-xs text-emerald-800 font-medium">Total Deductible</p>
                <p className="mt-1 text-lg font-bold text-emerald-700">
                  {formatINR(summary.totalDeductible)}
                </p>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
                <p className="text-xs text-amber-800 font-medium">Tax You'll Save</p>
                <p className="mt-1 text-lg font-bold text-amber-700">
                  {formatINR(summary.taxSaved)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-border/40 bg-[var(--bg)] px-3 py-2.5">
              <TrendingUp className="h-4 w-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-foreground/70">
                Estimated based on <strong className="text-[var(--fg)]">{summary.slab}</strong> tax
                slab
              </p>
            </div>

            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-1.5">
                <FileText className="h-3.5 w-3.5" /> AI Tax Advice
              </p>
              <p className="text-xs text-emerald-800 leading-relaxed">{summary.advice}</p>
            </div>

            <button
              onClick={calculate}
              className="w-full text-center text-xs text-foreground/50 hover:text-[var(--maroon)] transition py-1"
            >
              Recalculate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
